import { ReactNode, useEffect, useRef } from 'react';
import { cn } from '@/lib/utils';
import { AuthThemeToggle } from './AuthThemeToggle';
import { AuthLanguageSwitcher } from './AuthLanguageSwitcher';
import { runAuthPageSafetyChecks } from '@/lib/security/authPageSafety';

interface AuthLayoutProps {
  children: ReactNode;
  className?: string;
}

export function AuthLayout({ children, className }: AuthLayoutProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Run security checks on auth pages
  useEffect(() => {
    runAuthPageSafetyChecks();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Check for reduced motion preference
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (media.matches) return;

    // Check if touch device (skip mouse tracking on mobile)
    const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    if (isTouchDevice) return;

    let ticking = false;

    const onMove = (e: MouseEvent) => {
      if (ticking) return;
      ticking = true;

      requestAnimationFrame(() => {
        const { innerWidth: w, innerHeight: h } = window;
        const x = e.clientX / w; // 0..1
        const y = e.clientY / h; // 0..1

        // Map to subtle offsets (max 10% shift) and a small angle sway (±10deg)
        const px = 50 + (x - 0.5) * 20; // 40%..60%
        const py = 50 + (y - 0.5) * 20; // 40%..60%
        const angle = 90 + (x - 0.5) * 20; // 80..100 deg

        el.style.setProperty('--gx', `${px}%`);
        el.style.setProperty('--gy', `${py}%`);
        el.style.setProperty('--gangle', `${angle}deg`);

        ticking = false;
      });
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => window.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div
      ref={containerRef}
      className={cn(
        'min-h-screen w-full flex items-center justify-center p-4 relative',
        'animate-gradient',
        'motion-reduce:animate-none motion-reduce:bg-gradient-to-br motion-reduce:from-background motion-reduce:to-muted/20',
        className
      )}
      style={
        {
          '--gx': '50%',
          '--gy': '50%',
          '--gangle': '90deg',
        } as React.CSSProperties
      }
    >
      {/* Top-right controls: Theme toggle + Language selector */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <AuthThemeToggle />
        <AuthLanguageSwitcher />
      </div>

      <div className="w-full max-w-[440px]">{children}</div>
    </div>
  );
}
